import * as React from "react"
import { useRecoilState, useRecoilValue, useSetRecoilState } from "recoil"
import {
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  Text,
  Box,
  Flex
} from "@chakra-ui/react"
import layoutStates, {
  changeTabSelector
} from "../../../../recoil/atoms/layout"
import {
  chatCountAtom,
  chatIsReadAtom,
  chatIsReadSelector
} from "../../../../recoil/atoms/chats"
import { hasPermission } from "../../../../util"
import { LocalUserProperties } from "../../../../types/localUserProps"
import { BadgesLiveMode } from "../../badgesLiveMode"
import MobileChat from "../mobileChat"
import { Icon } from "../../../common/icons"
import { shoppableVideosSelector } from "../../../../recoil/selectors/shoppable-videos-selector"
import {
  brandingSelector,
  roomSelector,
  showTabsAtBottomOnMobileSelector
} from "../../../../recoil/atoms/room"
import useWindowSize from "../../../../hooks/useWindowSize"
import {
  HEIGHT_SERVICE_PANEL_MOBILE_BOTTOM_TAB,
  HEIGHT_SERVICE_PANEL_MOBILE_BUTTON,
  MODAL_Z_INDEX
} from "../../../../constants"
import HandleChatCount from "../../../servicesPanel/chat/HandleChatCount"
import { IoChatbubbleSharp } from "react-icons/io5"
import { badgeHistoryListSelector } from "../../../../recoil/atoms/badgeHistory"
import Videos from "../../../servicesPanel/videos"
import { useTranslation } from "react-i18next"
import { shoppableMNPVideoAtom } from "../../../../recoil/atoms/shoppableVideos"
import { GiFairyWand, GiShoppingBag } from "react-icons/gi"
import { useLocalUserType } from "../../../../hooks/useLocalUserType"
import { taggedBadgesAtom } from "../../../../recoil/atoms/taggedBadges"
import { AvatarVideoSize } from "../../../../recoil/atoms/avatars/types"
import { initialVideoResolutionOnMobileValues } from "../../../../constants/enums"
import { Tabs as TabsT } from "../../../../recoil/atoms/layout"
import useConfig from "src/hooks/useConfig"
import {
  shoppableVideoConfigToPanelIcon,
  shoppableVideoConfigToPanelTitleText
} from "src/components/servicesPanel/util"
import { AiOutlineControl } from "react-icons/ai"
import usePlayerVisibility from "src/recoil/atoms/player/usePlayerVisibility"
import { useCurrentAvatarInfo } from "src/conditionals/Avatar"
import { InspirationsTabView } from "src/components/servicesPanel/inspirations"
import { nativeMobileSafeAreaAtom } from "src/recoil/atoms/nativeMobileSafeArea"
import { findSideRatio } from "src/util/findSideRatio"
import {
  InspirationsButtonIcon,
  inspirationsButtonText
} from "src/shared/components/InspirationsButtonContent"

interface MobileServicePanelProps {
  isTransparent?: boolean
  chatSize: {
    width: number | null
    height: number | null
  }
  localUserProperties: LocalUserProperties
  hideTabs: boolean
  isTablet: boolean
  isLandscape?: boolean
}

const UserPermission = window.MeetOnline?.UserPermission

const ChatTab: React.FC<{
  enabled: boolean
  height?: string
  isTopBar?: boolean
  hasPermission?: boolean
  onClick?: React.MouseEventHandler<HTMLButtonElement>
}> = ({ enabled, height, isTopBar, hasPermission, onClick }) => {
  const roomStyles = useRecoilValue(brandingSelector)
  const chatIsRead = useRecoilValue(chatIsReadAtom)
  const chatCount = useRecoilValue(chatCountAtom)
  const { t } = useTranslation()

  return (
    <Tab
      onClick={onClick}
      display={enabled ? "flex" : "none"}
      height={height}
      color={roomStyles?.accessButtonTextColor}
      stroke={roomStyles?.accessButtonTextColor}
      borderBottom={isTopBar && `2px solid !important`}
      boxSizing="border-box"
      lineHeight="1"
      fontWeight="medium"
      _selected={{
        borderBottom: isTopBar && `2px solid !important`,
        backgroundColor: roomStyles?.accessButtonSelectedColor ?? "#edf2f7",
        color: roomStyles?.accessButtonSelectedTextColor ?? "#000",
        borderColor: roomStyles?.accessButtonSelectedTextColor ?? "#000",
        stroke: roomStyles?.accessButtonSelectedTextColor ?? "#000"
      }}
      width="100%"
      fontSize={"15px"}
      _focus={{}}
    >
      <Box pos="relative" display="flex" marginRight="0.5rem">
        <Flex id="container">
          <Flex pos="relative">
            <Icon
              src={IoChatbubbleSharp}
              boxSize="1rem"
              fontSize="1rem"
              color="transparent"
              strokeWidth="3rem"
              stroke={"inherit"}
              lineHeight={1}
              flexShrink="unset"
            />
            <Box
              display="flex"
              alignItems="center"
              justifyContent="center"
              position="absolute"
              width="18px"
              height="18px"
              top={chatCount > 99 ? "-4px" : "-1.3px"}
              left="-1.1px"
              fontSize={chatCount > 9 ? "0.6rem" : "0.5rem"}
              fontWeight={chatCount > 99 ? "bold" : "unset"}
            >
              {chatCount > 99 ? "..." : chatCount}
            </Box>
            {!chatIsRead && hasPermission && (
              <Box
                width={"8px"}
                height={"8px"}
                borderRadius="50%"
                background={"red"}
                position="absolute"
                top="-4px"
                right="-3px"
              ></Box>
            )}
          </Flex>
        </Flex>
      </Box>
      <Flex>{t("Chat")}</Flex>
    </Tab>
  )
}

const _MobileServicePanel: React.FC<MobileServicePanelProps> = ({
  chatSize,
  isTransparent,
  localUserProperties,
  hideTabs,
  isTablet,
  isLandscape
}) => {
  const { t } = useTranslation()
  const room = useRecoilValue(roomSelector)
  const roomStyles = useRecoilValue(brandingSelector)
  const avatarInfo = useCurrentAvatarInfo()
  const [{ enabledShoppableMNP }] = useRecoilState(shoppableMNPVideoAtom)
  const setChatIsRead = useSetRecoilState<boolean>(chatIsReadSelector)
  const { isModerator } = useLocalUserType()
  const allBadgeHistory = useRecoilValue(badgeHistoryListSelector)
  const taggedBadgesList = useRecoilValue(taggedBadgesAtom)
  const { screenSize } = useWindowSize()
  const { isPlayerVisible, togglePlayerVisibility } = usePlayerVisibility()

  const showTabsAtBottomOnMobile = useRecoilValue(
    showTabsAtBottomOnMobileSelector
  )
  const config = useConfig()
  const isTopBar = !showTabsAtBottomOnMobile
  const isShoppableVideos = useRecoilValue(shoppableVideosSelector)

  const isBadges = React.useMemo(() => {
    const canSeeHistory = hasPermission(
      localUserProperties?.permissions,
      UserPermission.canSeeHistory
    )
    const hasProductToShow =
      allBadgeHistory.length !== 0 || taggedBadgesList.length !== 0
    return (
      (canSeeHistory && hasProductToShow) || // For Broadcaster and Audience condition
      isModerator
    )
  }, [
    allBadgeHistory.length,
    taggedBadgesList.length,
    isModerator,
    localUserProperties?.permissions
  ])

  // Display the badge tab even if the badge is not present when the bottom tab is active.
  const showBadgeTab = isBadges || showTabsAtBottomOnMobile

  const canSeeChat = hasPermission(
    localUserProperties?.permissions,
    UserPermission.canUseChat
  )

  const isChat = React.useMemo(() => {
    return (
      showTabsAtBottomOnMobile || // always when bottom tab is active
      canSeeChat // show depending on user access permission
    )
  }, [canSeeChat, showTabsAtBottomOnMobile])

  // Tab Index mapping is generated base on the config
  const tabIndexMap = React.useMemo(() => {
    let index = 0
    const indexMap = {
      ...(isChat && { chat: index++ }),
      ...(isShoppableVideos && { watch: index++ }),
      ...(config.sidepanelInspirationsEnabled && { inspirations: index++ }),
      ...(config.sidepanelFeaturedEnabled && { shop: index++ }),
      ...(config.sidepanelSettingsEnabled && { control: index++ }),
      closed: -1
    }

    return indexMap
  }, [isShoppableVideos, config])

  const [tab] = useRecoilState(layoutStates.activeTab)
  const [previousTab] = useRecoilState(layoutStates.previousTab)

  const setTab = useSetRecoilState(changeTabSelector)

  // This useEffect is used to select the previous tab after exiting fullscreen interface
  React.useEffect(() => {
    if (tab === "closed" && isTransparent === false) {
      setTab(previousTab)
    }
  }, [isTransparent])

  // useEffect to change the tab to previous tab when shoppable video is disabled from the dashboard
  React.useEffect(() => {
    if (!room.enableShoppableVideos && tab === "watch") {
      setTab(previousTab)
    }
  }, [room.enableShoppableVideos])

  const isDefaultVideoSizeFullScreen = avatarInfo.avatarId
    ? avatarInfo.videoSize === AvatarVideoSize.fullscreen
    : room?.initialVideoResolutionMobilePortrait ===
      initialVideoResolutionOnMobileValues.FULLSCREEN

  // This useEffect is used to close the tab when the main video size is set to fullscreen.
  React.useEffect(() => {
    if (isDefaultVideoSizeFullScreen) {
      setTab("closed")
    }
  }, [isDefaultVideoSizeFullScreen])

  const handleTabsChangeTo = React.useCallback(
    (tab: TabsT) => {
      // Set changed tab
      setTab(currentTab => {
        // If the user clicks the active tab and video is in fullscreen mode, then close the tab.
        if (currentTab === tab && isTransparent) {
          return "closed"
        }
        return tab
      })
    },
    [isTransparent]
  )

  const isTabs = React.useMemo(() => {
    return (
      !isTransparent &&
      !hideTabs &&
      ([isBadges, isChat, tab === "watch"].filter(item => item).length > 1 ||
        isModerator)
    )
  }, [hideTabs, isBadges, isChat, isTransparent, isModerator])

  const tabHeight =
    showTabsAtBottomOnMobile && `${HEIGHT_SERVICE_PANEL_MOBILE_BOTTOM_TAB}px`

  const tabPanelHeight =
    chatSize.height -
    // minus Bottom tab or tab height which ever is present
    (showTabsAtBottomOnMobile
      ? HEIGHT_SERVICE_PANEL_MOBILE_BOTTOM_TAB
      : isTabs
      ? HEIGHT_SERVICE_PANEL_MOBILE_BUTTON
      : 0)

  const bottomTabStyle = showTabsAtBottomOnMobile
    ? ({
        width: "100vw",
        position: "fixed",
        zIndex: MODAL_Z_INDEX,
        bottom: "0px"
      } as const)
    : {}

  const safeArea = useRecoilValue(nativeMobileSafeAreaAtom)

  const renderTabList = () => (
    <TabList
      style={bottomTabStyle}
      // On utta platform, don't set the bg color
      bg={
        config.platformName !== "Utta" &&
        roomStyles?.accessButtonBackgroundColor
      }
      marginBottom={safeArea.enableBottomSafeArea && safeArea.bottom + "px"}
      zIndex={1000000000000}
    >
      <ChatTab
        enabled={isChat}
        height={tabHeight}
        isTopBar={isTopBar}
        hasPermission={canSeeChat}
        onClick={() => {
          handleTabsChangeTo("chat")
          setChatIsRead(true)
        }}
      />
      {isShoppableVideos && (
        <Tab
          onClick={() => handleTabsChangeTo("watch")}
          height={tabHeight}
          display="flex"
          lineHeight="1"
          fontWeight="medium"
          fontSize={"15px"}
          color={
            enabledShoppableMNP
              ? roomStyles?.secondaryAccessButtonSelectedTextColor
              : roomStyles?.accessButtonTextColor
          }
          bg={
            enabledShoppableMNP &&
            roomStyles?.secondaryAccessButtonSelectedColor
          }
          borderBottom={isTopBar && `2px solid !important`}
          _selected={{
            backgroundColor: enabledShoppableMNP
              ? roomStyles?.secondaryAccessButtonSelectedColor
              : roomStyles?.accessButtonSelectedColor ?? "#edf2f7",
            color: enabledShoppableMNP
              ? roomStyles?.secondaryAccessButtonSelectedTextColor
              : roomStyles?.accessButtonSelectedTextColor ?? "#000",
            borderBottom: isTopBar && `2px solid !important`,
            borderColor: roomStyles?.accessButtonSelectedTextColor ?? "#000"
          }}
          width="100%"
          _focus={{}}
        >
          <Icon
            src={shoppableVideoConfigToPanelIcon(config.shoppableVideos)}
            marginRight="0.5rem"
          />
          <Text>
            {t(shoppableVideoConfigToPanelTitleText(config.shoppableVideos))}
          </Text>
        </Tab>
      )}

      {config.sidepanelInspirationsEnabled && (
        <Tab
          onClick={() => handleTabsChangeTo("inspirations")}
          height={tabHeight}
          display="flex"
          lineHeight="1"
          fontWeight="medium"
          fontSize={"15px"}
          color={roomStyles?.accessButtonTextColor}
          borderBottom={isTopBar && `2px solid !important`}
          _selected={{
            borderBottom: isTopBar && `2px solid !important`,
            backgroundColor: roomStyles?.accessButtonSelectedColor ?? "#edf2f7",
            color: roomStyles?.accessButtonSelectedTextColor ?? "#000",
            borderColor: roomStyles?.accessButtonSelectedTextColor ?? "#000"
          }}
          width="100%"
          _focus={{}}
        >
          <InspirationsButtonIcon marginRight="0.5rem" />
          <Text>{inspirationsButtonText(config)}</Text>
        </Tab>
      )}

      {config.sidepanelFeaturedEnabled && (
        <Tab
          onClick={() => handleTabsChangeTo("shop")}
          height={tabHeight}
          display="flex"
          lineHeight="1"
          fontWeight="medium"
          fontSize={"15px"}
          color={roomStyles?.accessButtonTextColor}
          borderBottom={isTopBar && `2px solid !important`}
          _selected={{
            borderBottom: isTopBar && `2px solid !important`,
            backgroundColor: roomStyles?.accessButtonSelectedColor ?? "#edf2f7",
            color: roomStyles?.accessButtonSelectedTextColor ?? "#000",
            borderColor: roomStyles?.accessButtonSelectedTextColor ?? "#000"
          }}
          width="100%"
          _focus={{}}
        >
          <Icon
            src={GiShoppingBag}
            marginRight="0.5rem"
            width="17px"
            height="17px"
          />
          <Text>{t("Shop")}</Text>
        </Tab>
      )}

      {config.sidepanelSettingsEnabled && (
        <Tab
          height={tabHeight}
          display="flex"
          lineHeight="1"
          fontWeight="medium"
          fontSize={"15px"}
          color={roomStyles?.accessButtonTextColor}
          borderBottom={isTopBar && `2px solid !important`}
          {...(isPlayerVisible && {
            borderBottom: isTopBar && `2px solid !important`,
            // backgroundColor: roomStyles?.accessButtonSelectedColor ?? "#edf2f7",
            color: roomStyles?.accessButtonSelectedTextColor ?? "#000",
            borderColor: roomStyles?.accessButtonSelectedTextColor ?? "#000"
          })}
          _focus={{}}
          // Toggle the player panel focus.
          onClick={() => togglePlayerVisibility()}
        >
          <Icon src={AiOutlineControl} width="17px" height="17px" />
        </Tab>
      )}
    </TabList>
  )

  const servicePanelHeight = isLandscape
    ? 300
    : isTransparent
    ? 250
    : tabPanelHeight

  return (
    <>
      <HandleChatCount />
      <Tabs
        index={tabIndexMap[tab]}
        lazyBehavior="keepMounted"
        variant={showTabsAtBottomOnMobile && "unstyled"}
      >
        {isTabs && !showTabsAtBottomOnMobile && renderTabList()}
        <TabPanels
          position={isTransparent ? "absolute" : "unset"}
          bottom={
            isLandscape
              ? isPlayerVisible
                ? "52px"
                : "0px"
              : isPlayerVisible
              ? "102px"
              : "52px"
          }
          zIndex={isTransparent ? 100000 : "unset"}
          style={{
            width: isLandscape
              ? findSideRatio(screenSize.height, 16, 9) - 100 + "px"
              : "100%"
          }}
          left={isLandscape && "50%"}
          right={isLandscape && "32px"}
          transform={isLandscape && "translate(-50%, 0px)"}
        >
          <TabPanel padding={"0px"}>
            <MobileChat
              isLandscape={isLandscape}
              chatSize={{
                height: servicePanelHeight,
                // isTransparent ? servicePanelHeight :
                //   chatSize.height -
                //   (showTabsAtBottomOnMobile
                //     ? HEIGHT_SERVICE_PANEL_MOBILE_BOTTOM_TAB
                //     : 0),
                width: chatSize.width
              }}
              isTransparent={isTransparent}
              isTablet={isTablet}
              hideTabs={false}
              isTabs={isTabs}
              nameLocalUser={localUserProperties.name}
              localUserProperties={localUserProperties}
            />
          </TabPanel>

          {isShoppableVideos && (
            <TabPanel padding={"0px"} height={servicePanelHeight}>
              <Videos isMobile={true} height={servicePanelHeight} />
            </TabPanel>
          )}

          {config.sidepanelInspirationsEnabled && (
            <TabPanel
              padding={"0px"}
              height={servicePanelHeight}
              overflowY="hidden"
            >
              <InspirationsTabView openChat={() => setTab("chat")} />
            </TabPanel>
          )}

          {config.sidepanelFeaturedEnabled && (
            <TabPanel
              padding={"0px"}
              height={
                servicePanelHeight
                // showTabsAtBottomOnMobile &&
                // chatSize.height - HEIGHT_SERVICE_PANEL_MOBILE_BOTTOM_TAB
              }
            >
              <BadgesLiveMode
                isMobile={true}
                isTablet={isTablet}
                width={screenSize.width - 20}
                height={tabPanelHeight}
              />
            </TabPanel>
          )}
        </TabPanels>
        {showTabsAtBottomOnMobile && !isLandscape && renderTabList()}
      </Tabs>
    </>
  )
}

export const MobileServicePanel = React.memo(_MobileServicePanel)
